/**
 * Theme runtime API
 * -----------------------------------------------------------------------
 * Tokens live in `tokens.css`. This module provides:
 *   - Typed token names (autocomplete when referring to a token in code)
 *   - `setTheme(name)` — swap the whole palette (night / console / …)
 *   - `setAccentHue(h)` — nudge the brand hue without touching any other token
 *
 * Single-knob retuning: call setAccentHue(200) to turn the accent cyan-ish,
 * setAccentHue(140) to turn it green, etc. All accent/accent-soft/accent-hover
 * variants are derived from --accent-h in tokens.css.
 */

export type ThemeName = 'night' | 'console'

export const THEMES: readonly ThemeName[] = ['night', 'console'] as const

export const COLOR_TOKENS = [
  'bg',
  'bg-2',
  'panel',
  'panel-2',
  'line',
  'line-strong',
  'fg',
  'fg-muted',
  'fg-faint',
  'accent',
  'accent-soft',
  'accent-hover',
  'accent-fg',
  'on-accent-soft',
  'ok',
  'ok-soft',
  'warn',
  'warn-soft',
  'bad',
  'bad-soft',
  'info',
] as const

export type ColorToken = (typeof COLOR_TOKENS)[number]

/** CSS var reference for a token. Use with inline `style` when Tailwind utilities aren't a fit. */
export function tokenVar(token: ColorToken | 'gap' | 'row' | 'cell-x' | 'cell-y'): string {
  return `var(--${token})`
}

/** Default theme. Dark-first design — Night Ops is the canonical look,
 *  light is opt-in. The default is *not* persisted: only an explicit pick
 *  of the non-default theme writes to localStorage. Picking dark again
 *  clears the entry, so a user who never opts in stays dark forever. */
export const DEFAULT_THEME: ThemeName = 'night'

/** Swap the entire theme (night ↔ console). Persists *only* when the user
 *  picks the non-default; picking the default removes the entry so dark
 *  remains a "no-entry" default and can't be locked in by a stale value. */
export function setTheme(name: ThemeName): void {
  const root = document.documentElement
  THEMES.forEach((t) => root.classList.remove(`theme-${t}`))
  root.classList.add(`theme-${name}`)
  try {
    if (name === DEFAULT_THEME) {
      localStorage.removeItem('cm.theme')
    } else {
      localStorage.setItem('cm.theme', name)
    }
  } catch {
    /* private mode / quota */
  }
}

/** Read the theme the user last picked, or the default when none is stored. */
export function getStoredTheme(): ThemeName {
  try {
    const stored = localStorage.getItem('cm.theme')
    if (stored === 'night' || stored === 'console') return stored
  } catch {
    /* private mode */
  }
  return DEFAULT_THEME
}

/** Shift accent hue at runtime. Range 0-360. Non-persistent by default. */
export function setAccentHue(hue: number, persist = false): void {
  document.documentElement.style.setProperty('--accent-h', String(hue))
  if (persist) {
    try {
      localStorage.setItem('cm.accent-h', String(hue))
    } catch {
      /* ignore */
    }
  }
}

/** Initialise theme on app boot. Call once in main.tsx before rendering. */
export function initTheme(): void {
  setTheme(getStoredTheme())
  try {
    const storedHue = localStorage.getItem('cm.accent-h')
    if (storedHue) setAccentHue(Number(storedHue))
  } catch {
    /* ignore */
  }
}
